/**
 * The wizard's answers, kept across a reload — customer-portal review #15 (fix round D,
 * 2026-09-25).
 *
 * A reload, or any crash, used to lose the whole draft: the menu, the destination and every field.
 * Now every answer is written to the tab's `sessionStorage` as it changes, under the workspace and
 * the template (`wetalk.new-agent-draft.v1.<workspace>.<template>`), and read back when the wizard
 * opens. Session storage rather than local storage: the draft survives a reload and a crash of this
 * tab, and is gone when the tab is closed — a business's menu and hours are not left on a shared
 * computer for the next person.
 *
 * THE FILES. A `File` cannot be written to session storage, so the picked documents go to IndexedDB
 * (which stores a `File` as it is) under the same key and the field code. Where IndexedDB is not
 * there or refuses — a private window, a blocked site — the draft still keeps the file's NAME, and
 * the content step says "attach it again" instead of pretending the file is still attached.
 *
 * THE AGENT ALREADY CREATED. When "Build the agent" created the VoiceAgent and an upload then
 * failed, the draft keeps that VoiceAgent's id and the uploads already confirmed, so the retry after
 * a reload resumes it rather than creating a second agent (plan-003 RC-32's rule, now across a
 * reload too).
 *
 * Every storage call is wrapped: storage may throw or be missing, and the wizard must work without
 * it. A successful build clears both.
 */
import type { AnsweringChoice, OpeningPeriod, OpeningWeek, SpecialDateEntry } from "./opening-hours.js";
import { isCalendarDateText, isWaitMinute, openingWeekOf } from "./opening-hours.js";

/**
 * How callers pay, in the words the build receives (`payment_method`). `online` joined with plan-005
 * OP-38: a payment link WeTalk collects and pays out, offered to a caller only while the account's
 * online payment is switched on — the hand-over methods beside it are what callers get otherwise.
 */
export const PAYMENT_METHOD_CODE = ["cash_on_delivery", "card_on_delivery", "online"] as const;
export type PaymentMethodCode = (typeof PAYMENT_METHOD_CODE)[number];
export type NumberChoice = "bring_my_own" | "new_number";

/** Everything the person answered. `null` means "not answered yet", so a default may apply. */
export interface WizardAnswer {
  /** `null` until the person types in the name box; then exactly what they typed. */
  readonly displayName: string | null;
  /** `text` fields as typed, and each `upload` field's file NAME. */
  readonly fieldValue: Readonly<Record<string, string>>;
  /** The opening-hours editor's week, per field code. */
  readonly openingWeek: Readonly<Record<string, OpeningWeek>>;
  readonly languageCode: readonly string[] | null;
  /** `""` until the person picks one. */
  readonly voiceId: string;
  /**
   * The voice each language speaks in, by language code (design sync 2026-10-09: "Each language has its own voices").
   * Absent until the person changes a language's voice; the wizard's defaults apply to every language not named.
   */
  readonly voiceByLanguage?: Readonly<Record<string, string>>;
  /** `""` means "use the suggestion" — unless {@link greetingEdited}, when it is exactly what the box holds. */
  readonly greeting: string;
  /**
   * `true` once the person typed in the greeting box (design sync 2026-10-09: the box starts filled with the suggestion
   * made from the business's own name, so an emptied box is an empty greeting, not "use the suggestion").
   */
  readonly greetingEdited?: boolean;
  readonly numberChoice: NumberChoice;
  readonly medium: readonly string[];
  /** `null` until the person picks one; then the default (within the tier) no longer applies. */
  readonly channelUnitCount: number | null;
  readonly recordingEnabled: boolean;
  /** `null` until chosen; `[]` is "the agent takes no payment". */
  readonly paymentMethod: readonly PaymentMethodCode[] | null;
}

/** A VoiceAgent "Build the agent" created, kept so a retry resumes it. */
export interface CreatedInDraft {
  readonly voiceAgentId: string;
  readonly buildJobId: string;
  /** Upload fields already confirmed for it. */
  readonly confirmedField: readonly string[];
}

export interface StoredDraft {
  readonly answer: WizardAnswer;
  readonly created: CreatedInDraft | null;
}

export function freshAnswer(): WizardAnswer {
  return {
    displayName: null,
    fieldValue: {},
    openingWeek: {},
    languageCode: null,
    voiceId: "",
    greeting: "",
    numberChoice: "bring_my_own",
    medium: ["voice"],
    channelUnitCount: null,
    recordingEnabled: true,
    paymentMethod: null,
  };
}

const KEY_PREFIX = "wetalk.new-agent-draft.v1";

export function draftKeyOf(workspaceId: string, agentTemplateCode: string): string {
  return `${KEY_PREFIX}.${workspaceId}.${agentTemplateCode}`;
}

/** The tab's storage, or `null` where the browser has none or refuses it. */
function tabStore(): Storage | null {
  try {
    // glossary-lint-disable-next-line session -- the DOM's own sessionStorage API, not a WeTalk session
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

export function saveDraft(key: string, draft: StoredDraft): void {
  try {
    tabStore()?.setItem(key, JSON.stringify(draft));
  } catch {
    // Storage full or refused: the draft lives on in memory, which is what happened before.
  }
}

export function clearDraft(key: string): void {
  try {
    tabStore()?.removeItem(key);
  } catch {
    // Nothing to clear where nothing could be written.
  }
}

function stringRecordOf(value: unknown): Record<string, string> | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return null;
  const found: Record<string, string> = {};
  for (const [key, one] of Object.entries(value as Record<string, unknown>)) {
    if (typeof one !== "string") return null;
    found[key] = one;
  }
  return found;
}

function stringListOf(value: unknown): string[] | null {
  return Array.isArray(value) && value.every((one) => typeof one === "string") ? (value as string[]) : null;
}

const TIME_TEXT = /^\d\d:\d\d$/;

function periodOfStored(value: unknown): OpeningPeriod | null {
  if (typeof value !== "object" || value === null) return null;
  const { opensAt, closesAt } = value as { opensAt?: unknown; closesAt?: unknown };
  return typeof opensAt === "string" && typeof closesAt === "string" && TIME_TEXT.test(opensAt) && TIME_TEXT.test(closesAt)
    ? { opensAt, closesAt }
    : null;
}

/** The stored special dates, or `null` when they are not the shape written. */
function specialDateOfStored(value: unknown): SpecialDateEntry[] | null {
  if (!Array.isArray(value)) return null;
  const found: SpecialDateEntry[] = [];
  for (const one of value) {
    if (typeof one !== "object" || one === null) return null;
    const { date, label, interval } = one as { date?: unknown; label?: unknown; interval?: unknown };
    if (typeof date !== "string" || !isCalendarDateText(date) || typeof label !== "string" || !Array.isArray(interval)) return null;
    const period = interval.map(periodOfStored);
    if (period.some((each) => each === null)) return null;
    found.push({ date, label, interval: period as OpeningPeriod[] });
  }
  return found;
}

/** The stored choices, or `null` when they are not the shape written. */
function answeringChoiceOfStored(value: unknown): AnsweringChoice | null {
  if (typeof value !== "object" || value === null) return null;
  const one = value as Record<string, unknown>;
  const after = one["afterHoursChoice"];
  const busy = one["busyChoice"];
  const wait = one["longestWaitMinute"];
  const forwardTo = one["forwardTo"];
  if (after !== "closed" && after !== "message" && after !== "forward") return null;
  if (busy !== "queue" && busy !== "message" && busy !== "forward") return null;
  if (wait !== null && !isWaitMinute(typeof wait === "number" ? wait : null)) return null;
  if (forwardTo !== null && typeof forwardTo !== "string") return null;
  return { afterHoursChoice: after, busyChoice: busy, longestWaitMinute: wait as AnsweringChoice["longestWaitMinute"], forwardTo };
}

/**
 * Reads the stored draft back. Anything that does not have the shape written above is dropped whole
 * — a half-read draft would put answers the person never gave into the build.
 */
export function loadDraft(key: string): StoredDraft | null {
  let raw: string | null;
  try {
    raw = tabStore()?.getItem(key) ?? null;
  } catch {
    return null;
  }
  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw) as { answer?: Record<string, unknown>; created?: unknown };
    const answer = parsed.answer;
    if (typeof answer !== "object" || answer === null) return null;

    const fieldValue = stringRecordOf(answer["fieldValue"]);
    const medium = stringListOf(answer["medium"]);
    const languageCode = answer["languageCode"] === null ? null : stringListOf(answer["languageCode"]);
    const paymentList = answer["paymentMethod"] === null ? null : stringListOf(answer["paymentMethod"]);
    const paymentMethod =
      paymentList === null
        ? answer["paymentMethod"] === null
          ? null
          : undefined
        : paymentList.every((one): one is PaymentMethodCode => (PAYMENT_METHOD_CODE as readonly string[]).includes(one))
          ? paymentList
          : undefined;
    const weekRaw = answer["openingWeek"];
    const openingWeek: Record<string, OpeningWeek> = {};
    if (typeof weekRaw === "object" && weekRaw !== null) {
      for (const [code, week] of Object.entries(weekRaw as Record<string, unknown>)) {
        const read = openingWeekOf(week);
        if (read === null) return null;
        openingWeek[code] = read;
      }
    }
    const voiceByLanguageRaw = answer["voiceByLanguage"];
    const voiceByLanguage = voiceByLanguageRaw === undefined ? undefined : stringRecordOf(voiceByLanguageRaw);
    const greetingEdited = answer["greetingEdited"];
    // plan-015 AGC-UI: the answering answers (merged onto WizardAnswer in wizard-rules.ts), kept when well formed.
    const timeZone = answer["timeZone"];
    const specialDate = answer["specialDate"] === undefined ? undefined : specialDateOfStored(answer["specialDate"]);
    const answeringChoice = answer["answeringChoice"] === undefined ? undefined : answeringChoiceOfStored(answer["answeringChoice"]);
    const numberChoice = answer["numberChoice"];
    const channelUnitCount = answer["channelUnitCount"];
    if (
      fieldValue === null ||
      medium === null ||
      (answer["languageCode"] !== null && languageCode === null) ||
      paymentMethod === undefined ||
      (numberChoice !== "bring_my_own" && numberChoice !== "new_number") ||
      (channelUnitCount !== null && (typeof channelUnitCount !== "number" || !Number.isSafeInteger(channelUnitCount))) ||
      (answer["displayName"] !== null && typeof answer["displayName"] !== "string") ||
      typeof answer["voiceId"] !== "string" ||
      typeof answer["greeting"] !== "string" ||
      typeof answer["recordingEnabled"] !== "boolean" ||
      voiceByLanguage === null ||
      (greetingEdited !== undefined && typeof greetingEdited !== "boolean") ||
      (timeZone !== undefined && typeof timeZone !== "string") ||
      specialDate === null ||
      answeringChoice === null
    ) {
      return null;
    }

    let created: CreatedInDraft | null = null;
    if (parsed.created !== null && parsed.created !== undefined) {
      const one = parsed.created as Record<string, unknown>;
      const confirmedField = stringListOf(one["confirmedField"]);
      if (typeof one["voiceAgentId"] !== "string" || typeof one["buildJobId"] !== "string" || confirmedField === null) {
        return null;
      }
      created = { voiceAgentId: one["voiceAgentId"], buildJobId: one["buildJobId"], confirmedField };
    }

    return {
      answer: {
        displayName: answer["displayName"] as string | null,
        fieldValue,
        openingWeek,
        languageCode,
        voiceId: answer["voiceId"],
        ...(voiceByLanguage === undefined ? {} : { voiceByLanguage }),
        greeting: answer["greeting"],
        ...(greetingEdited === undefined ? {} : { greetingEdited: greetingEdited as boolean }),
        numberChoice,
        medium,
        channelUnitCount: channelUnitCount as number | null,
        recordingEnabled: answer["recordingEnabled"],
        paymentMethod,
        ...(typeof timeZone === "string" ? { timeZone } : {}),
        ...(specialDate === undefined ? {} : { specialDate }),
        ...(answeringChoice === undefined ? {} : { answeringChoice }),
      },
      created,
    };
  } catch {
    return null;
  }
}

// ── the picked files, in IndexedDB ────────────────────────────────────────────────────────────

const FILE_DATABASE = "wetalk-new-agent-draft";
const FILE_STORE = "file";

function openFileDatabase(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === "undefined") {
        resolve(null);
        return;
      }
      const request = indexedDB.open(FILE_DATABASE, 1);
      request.onupgradeneeded = () => {
        request.result.createObjectStore(FILE_STORE);
      };
      request.onsuccess = () => {
        resolve(request.result);
      };
      request.onerror = () => {
        resolve(null);
      };
      request.onblocked = () => {
        resolve(null);
      };
    } catch {
      resolve(null);
    }
  });
}

async function withFileStore<T>(mode: "readonly" | "readwrite", work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T | null> {
  const database = await openFileDatabase();
  if (database === null) return null;
  return new Promise((resolve) => {
    try {
      const request = work(database.transaction(FILE_STORE, mode).objectStore(FILE_STORE));
      request.onsuccess = () => {
        resolve(request.result);
        database.close();
      };
      request.onerror = () => {
        resolve(null);
        database.close();
      };
    } catch {
      resolve(null);
      database.close();
    }
  });
}

export async function saveDraftFile(key: string, fieldCode: string, file: File): Promise<void> {
  await withFileStore("readwrite", (store) => store.put(file, `${key}.${fieldCode}`));
}

/** The files kept for these fields; a field whose file was not kept is simply absent. */
export async function loadDraftFile(key: string, fieldCode: readonly string[]): Promise<Record<string, File>> {
  const found: Record<string, File> = {};
  for (const code of fieldCode) {
    const one = await withFileStore<unknown>("readonly", (store) => store.get(`${key}.${code}`));
    if (one instanceof File) found[code] = one;
  }
  return found;
}

export async function clearDraftFile(key: string, fieldCode: readonly string[]): Promise<void> {
  for (const code of fieldCode) {
    await withFileStore("readwrite", (store) => store.delete(`${key}.${code}`));
  }
}
